import React, { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { GlobalContext } from "../../context/GlobalContext";
import "./Navbar.scss";

// ─── Menu Data ───────────────────────────────────────────────
// Primary: big bold links rendered center-stage
const PRIMARY_LINKS = [
  { label: "Home", to: "/" },
  { label: "Our Work", to: "/our-work" },
  { label: "AI Products", submenu: "aiProducts" },
  { label: "Services", submenu: "services" },
];

// Secondary: smaller links in a row beneath the primary list
const SECONDARY_LINKS = [
  // { label: "About Us", to: "/about-us" },
  // { label: "Meet the Team", to: "/meet-the-team" },
  // { label: "AI Readiness Survey", to: "/ai-readiness-survey" },
  // { label: "Careers", to: "/careers" },
];

const SUBMENUS = {
  aiProducts: {
    title: "AI Products",
    items: [
      { label: "Omnix", to: "/omnix" },
      { label: "Concord", to: "/concord" },
      { label: "InteliHire", to: "/intelihire" },
    ],
  },
  services: {
    title: "Services",
    items: [
      { label: "Brand Services", to: "/services" },
      { label: "AI Development", to: "/ai-development" },
      { label: "Custom Development", to: "/custom-software-development" },
      { label: "Offshore Development", to: "/offshore-software-development" },
    ],
  },
};

// ─── Component ───────────────────────────────────────────────
const Navbar = ({ url }) => {
  const { isHeroActive } = useContext(GlobalContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // Navbar scroll shrink
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openMenu = () => {
    setActiveSubMenu(null);
    setHoveredIndex(null);
    setMenuOpen(true);
  };

  const closeMenu = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setMenuOpen(false);
      setIsAnimatingOut(false);
      setActiveSubMenu(null);
      setHoveredIndex(null);
    }, 420);
  };

  const handleLinkClick = () => closeMenu();

  const goToSubmenu = (key) => {
    setHoveredIndex(null);
    setActiveSubMenu(key);
  };

  const backToMain = () => {
    setActiveSubMenu(null);
    setHoveredIndex(null);
  };

  const anyHovered = hoveredIndex !== null;

  return (
    <>
      {/* ── Top Bar ─────────────────────────────────────── */}
      <nav className={`navbar-wrap ${isScrolled ? "scrolled" : ""}`}>
        <div className="navbar-inner">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <img
              loading="lazy"
              src={`${url}images/exd_digital.webp`}
              alt="ExD Digital"
              className={`navbar-logo-img${!isHeroActive ? " logo-shrunk" : ""}`}
            />
          </Link>

          {/* Right controls */}
          <div className="navbar-controls">
            <HashLink
              className="navbar-cta desktop-only"
              smooth
              to="/contact-us#contact-form"
            >
              Get In Touch
            </HashLink>

            {/* Hamburger */}
            <button
              className={`hamburger ${menuOpen ? "is-open" : ""}`}
              onClick={menuOpen ? closeMenu : openMenu}
              aria-label="Toggle menu"
            >
              <span className="ham-line" />
              <span className="ham-line" />
              <span className="ham-line" />
            </button>
          </div>
        </div>
      </nav>

      {/* ── Full-screen Overlay ──────────────────────────── */}
      {(menuOpen || isAnimatingOut) && (
        <div className={`nav-overlay ${isAnimatingOut ? "closing" : "opening"}`}>
          {/* Overlay top bar */}
          <div className="overlay-topbar">
            <Link to="/" className="navbar-logo" onClick={handleLinkClick}>
              <img
                src={`${url}images/exd_digital.webp`}
                alt="ExD Digital"
                className="navbar-logo-img"
              />
            </Link>
            <button className="close-btn" onClick={closeMenu} aria-label="Close menu">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <line x1="4" y1="4" x2="24" y2="24" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
                <line x1="24" y1="4" x2="4" y2="24" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* ── Main menu list ── */}
          {!activeSubMenu && (
            <div className="overlay-menu">
              {/* Primary big links */}
              <ul
                className="menu-list"
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {PRIMARY_LINKS.map((link, i) => (
                  <li
                    key={link.label}
                    className={`menu-item ${anyHovered && hoveredIndex !== i ? "dimmed" : ""} ${hoveredIndex === i ? "active-hover" : ""}`}
                    onMouseEnter={() => setHoveredIndex(i)}
                    style={{ animationDelay: `${0.05 + i * 0.06}s` }}
                  >
                    {link.submenu ? (
                      <button
                        className="menu-link has-sub"
                        onClick={() => goToSubmenu(link.submenu)}
                      >
                        {link.label}
                        <svg className="chevron" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    ) : (
                      <Link className="menu-link" to={link.to} onClick={handleLinkClick}>
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>

              {/* Secondary small links — row beneath primary */}
              <ul className="secondary-list">
                {SECONDARY_LINKS.map((link, i) => (
                  <li key={link.label} className="secondary-item" style={{ animationDelay: `${0.35 + i * 0.07}s` }}>
                    <Link className="secondary-link" to={link.to} onClick={handleLinkClick}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Contact info at bottom */}
              <div className="overlay-contact">
                <span className="contact-label d-none">Contact Us</span>
                <a className="contact-email d-none" href="mailto:contact@exd.com">
                  contact@exd.com
                </a>
              </div>
            </div>
          )}

          {/* ── Submenu ── */}
          {activeSubMenu && SUBMENUS[activeSubMenu] && (
            <div className="overlay-submenu">
              {/* Back + title */}
              <div className="submenu-header">
                <button className="back-btn" onClick={backToMain}>
                  <svg width="32" height="32" viewBox="3 0 24 24" fill="none" stroke="#FCB61D" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <h3 className="submenu-title">{SUBMENUS[activeSubMenu].title}</h3>
              </div>

              {/* Submenu items */}
              <ul
                className="menu-list submenu-list"
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {SUBMENUS[activeSubMenu].items.map((item, i) => (
                  <li
                    key={item.label}
                    className={`menu-item ${anyHovered && hoveredIndex !== i ? "dimmed" : ""} ${hoveredIndex === i ? "active-hover" : ""}`}
                    onMouseEnter={() => setHoveredIndex(i)}
                    style={{ animationDelay: `${0.04 + i * 0.06}s` }}
                  >
                    <Link className="menu-link" to={item.to} onClick={handleLinkClick}>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Mobile sticky CTA ──────────────────────────── */}
      {/* {!menuOpen && (
        <HashLink
          className="mobile-cta"
          smooth
          to="/contact-us#contact-form"
        >
          Get In Touch
        </HashLink>
      )} */}
    </>
  );
};

export default Navbar;